"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import "./chatbot.css";
import { useUser } from "../../contexts/UserContext";
import chatbotService from "../../services/chatbot";
import { userService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";

// persistent state
function usePersistentState(key, defaultValue, storage = localStorage) {
    const [state, setState] = useState(() => {
        try {
            const stored = storage.getItem(key);
            return stored ? JSON.parse(stored) : defaultValue;
        } catch {
            return defaultValue;
        }
    });

    const setPersistentState = useCallback((value) => {
        setState(prev => {
            const next = typeof value === 'function' ? value(prev) : value;
            try {
                storage.setItem(key, JSON.stringify(next));
            } catch (e) {
                console.warn('Storage write failed:', e);
            }
            return next;
        });
    }, [key, storage]);

    return [state, setPersistentState];
}

// variants for the chatbot page
const chatbotVariants = {
    enter: {
        y: "-100%",   // starts above the screen
        opacity: 0,
    },
    center: {
        y: 0,
        opacity: 1,
        transition: { duration: 0.4, ease: [0.32, 0.72, 0, 1] }
    },
    exit: {
        y: "-100%",    // exits above the screen
        opacity: 0,
        transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] }
    }
};

export default function ChatBot() {
    const { user, roleData, logout } = useUser();
    const [conversations, setConversations] = useState([]);
    const [activeConversationId, setActiveConversationId] = usePersistentState(`chat_active_session_${user?.gtid}`, null);
    const [input, setInput] = useState("");
    const inputRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [loading, setLoading] = useState(false);
    const [hasChatConsent, setHasChatConsent] = useState(null); // null = unknown, true/false once checked
    const [consentCheckCount, setConsentCheckCount] = useState(0); // Force re-check when navigating to page
    const { login, loading: authLoading } = useAuth();
    const [exiting, setExiting] = useState(false);
    const navigate = useNavigate();


    const activeConversation = conversations.find(
        (c) => c.session_id === activeConversationId
    );

    // Auto-expand textarea as the user types (up to max-height, then scroll)
    useEffect(() => {
        const ta = inputRef.current;
        if (!ta) return;
        ta.style.height = "auto";
        ta.style.height = `${Math.max(48, Math.min(ta.scrollHeight, 200))}px`;
    }, [input]);

    // Mark redirect as used when landing on lab buddy, then load sessions
        useEffect(() => {
                try {
                        const redirect = sessionStorage.getItem('postLoginRedirect') || localStorage.getItem('postLoginRedirect');
                        if (redirect === '/lab-buddy') {
                                sessionStorage.setItem('postLoginRedirectUsed', 'true');
                                localStorage.setItem('postLoginRedirectUsed', 'true');
                        }
                } catch (_) {}
        }, []);

    // Load existing sessions only after user has accepted consent
    useEffect(() => {
        if (user && hasChatConsent === true) {
            loadSessions();
        }
    }, [user, hasChatConsent]);

    // Check chat consent when user changes or when navigating to chatbot (consentCheckCount)
    useEffect(() => {
        if (!user) {
            setHasChatConsent(null);
            return;
        }

        let cancelled = false;
        const checkChatConsent = async () => {
            // Prefer gtid so backend hashes correctly; consent APIs expect raw GTID
            const id = user?.gtid ?? user?.loginID ?? user?.username;
            if (!id) {
                setHasChatConsent(false);
                return;
            }
            try {
                const res = await userService.getChatConsentGiven(id);
                const value = typeof res === "object" && res !== null
                    ? !!(res.chat_consent ?? res.chatConsent)
                    : !!res;
                if (!cancelled) setHasChatConsent(value);
            } catch (error) {
                console.error("Failed to check chat consent:", error);
                // Don't clear consent on error if user already consented (e.g. transient network failure)
                if (!cancelled) setHasChatConsent((prev) => (prev === true ? true : false));
            }
        };

        checkChatConsent();
        return () => {
            cancelled = true;
        };
    }, [user, consentCheckCount]);

    // Re-check chat consent when user returns to the tab (visibility / focus)
    useEffect(() => {
        if (document.visibilityState === "visible") setConsentCheckCount((c) => c + 1);
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") setConsentCheckCount((c) => c + 1);
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, []);

    const loadSessions = async () => {
        try {
        const sessions = await chatbotService.getSessions();
        setConversations(sessions);
        
        // If there are sessions, load the first one on inital load or last opened if lab buddy opened previously
        if (sessions.length > 0) {
            const savedID = localStorage.getItem(`chat_active_session_${user?.gtid}`);
            const parsedId = savedID ? JSON.parse(savedID) : null;
            console.log("parsedID:", parsedId);
            const session_details = sessions.find(s => s.session_id === parsedId);
            console.log("session details:", session_details);
            const target = session_details ?? sessions[0];
            await loadSessionMessages(target.session_id);
        }
        } catch (error) {
        console.error("Failed to load sessions:", error);
        }
    };

    const loadSessionMessages = async (sessionId) => {
        try {
        const sessionData = await chatbotService.getSessionMessages(sessionId);
        // Normalize so messages have id (UI uses message.id); API returns message_id
        const messages = (sessionData.messages || []).map((m) => ({
            ...m,
            id: m.id ?? m.message_id,
        }));

        setConversations(prev =>
            prev.map(conv =>
            conv.session_id === sessionId
                ? { ...conv, messages }
                : conv
            )
        );
        setActiveConversationId(sessionId);

        setTimeout(() => {
            const container = scrollContainerRef.current;
            if (!container) return;
            container.scrollTop = container.scrollHeight;
        }, 50);

        } catch (error) {
        console.error("Failed to load messages:", error);
        }
    };

    const handleNavigateAway = (path) => {
        setExiting(true);
        setTimeout(() => navigate(path), 200); // match your exit duration
    };

    /** Create a local-only "New Chat" placeholder; real session is created when user sends first message */
    const createNewChat = () => {
        const pendingId = `pending-${Date.now()}`;
        const placeholder = {
            session_id: pendingId,
            title: "New Chat",
            messages: [],
            _pending: true,
        };
        setConversations(prev => [placeholder, ...prev]);
        setActiveConversationId(pendingId);
    };

        const redirectToSSO = () => {
        // Use main app's OIDC flow and pass redirect back to lab buddy route
        login('/lab-buddy');
        };

    const acceptChatConsent = async () => {
        const id = user?.gtid ?? user?.loginID ?? user?.username;
        if (!id) return;
        try {
            await userService.setChatConsentGiven(id);
            setHasChatConsent(true);
        } catch (error) {
            console.error("Failed to set chat consent:", error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!input.trim() || !activeConversationId) return;

        const userMessageContent = input.trim();
        setInput("");
        setLoading(true);

        let sessionIdToUse = activeConversationId;
        const isPending = activeConversationId.startsWith("pending-") || activeConversation?._pending;

        const optimisticUser = {
            id: `temp-user-${Date.now()}`,
            role: "user",
            content: userMessageContent,
        };
        const loadingPlaceholder = {
            id: `temp-loading-${Date.now()}`,
            role: "assistant",
            content: "",
            _loading: true,
        };

        const addOptimisticMessages = (targetSessionId) =>
            setConversations((prev) =>
                prev.map((conv) => {
                    if (conv.session_id !== targetSessionId) return conv;
                    return {
                        ...conv,
                        messages: [...conv.messages, optimisticUser, loadingPlaceholder],
                    };
                })
            );

        try {
            if (isPending) {
                const newSession = await chatbotService.createSession();
                sessionIdToUse = newSession.session_id;
                setConversations((prev) =>
                    prev.map((c) =>
                        c.session_id === activeConversationId
                            ? { ...newSession, messages: [optimisticUser, loadingPlaceholder] }
                            : c
                    )
                );
                setActiveConversationId(sessionIdToUse);
            } else {
                addOptimisticMessages(activeConversationId);
            }

            const { user_message, assistant_message } = await chatbotService.sendMessage(
                sessionIdToUse,
                userMessageContent
            );

            const newTitle =
                userMessageContent.length > 30
                    ? userMessageContent.slice(0, 30) + "..."
                    : userMessageContent;

            setConversations((prev) =>
                prev.map((conv) => {
                    if (conv.session_id !== sessionIdToUse) return conv;
                    const withoutTemp = conv.messages.filter(
                        (m) => !String(m.id).startsWith("temp-")
                    );
                    const isFirstMessage = withoutTemp.length === 0;
                    return {
                        ...conv,
                        title: isFirstMessage ? newTitle : conv.title,
                        messages: [
                            ...withoutTemp,
                            {
                                id: user_message.message_id,
                                role: user_message.role,
                                content: user_message.content,
                            },
                            {
                                id: assistant_message.message_id,
                                role: assistant_message.role,
                                content: assistant_message.content,
                            },
                        ],
                    };
                })
            );

            if (isPending) {
                await chatbotService.updateSessionTitle(sessionIdToUse, newTitle);
            } else if (activeConversation?.title === "New Chat" && activeConversation.messages.length === 0) {
                await chatbotService.updateSessionTitle(sessionIdToUse, newTitle);
            }
        } catch (error) {
            console.error("Failed to send message:", error);
            setInput(userMessageContent);
            setConversations((prev) =>
                prev.map((conv) => {
                    if (conv.session_id !== sessionIdToUse) return conv;
                    return {
                        ...conv,
                        messages: conv.messages.filter((m) => !String(m.id).startsWith("temp-")),
                    };
                })
            );
        } finally {
            setLoading(false);
        }
    };

    const deleteConversation = async (sessionId) => {
        const conv = conversations.find((c) => c.session_id === sessionId);
        const isPending = conv?._pending || sessionId.startsWith("pending-");

        if (isPending) {
            setConversations(prev => prev.filter(c => c.session_id !== sessionId));
            if (activeConversationId === sessionId) {
                const remaining = conversations.filter(c => c.session_id !== sessionId);
                setActiveConversationId(remaining.length > 0 ? remaining[0].session_id : null);
                if (remaining.length > 0 && !remaining[0]._pending) {
                    await loadSessionMessages(remaining[0].session_id);
                }
            }
            return;
        }

        try {
            await chatbotService.deleteSession(sessionId);
            setConversations(prev => prev.filter(c => c.session_id !== sessionId));
            if (activeConversationId === sessionId) {
                const remaining = conversations.filter(c => c.session_id !== sessionId);
                if (remaining.length > 0) {
                    await loadSessionMessages(remaining[0].session_id);
                } else {
                    setActiveConversationId(null);
                }
            }
        } catch (error) {
            console.error("Failed to delete conversation:", error);
        }
    };

    if (authLoading) {
        return (
            <div className="gt-chatbot">
                <div className="gt-auth-modal-backdrop" aria-busy="true">
                    <div className="gt-auth-modal gt-auth-modal--loading">
                        <div className="gt-consent-loading-spinner" aria-hidden="true" />
                        <p>Loading…</p>
                    </div>
                </div>
            </div>
        );
    }
    if (user && user.chatbot_access !== true) {
        return <Navigate to="/dashboard" replace />;
    }

    const needsAuth = !user;
    const checkingConsent = !!user && hasChatConsent === null; // still loading consent status
    const needsChatConsent = !!user && hasChatConsent === false; // only show gate when we know they haven't consented
    const showGate = needsAuth || needsChatConsent;

    return (
        <AnimatePresence>
            {!exiting && (
                <motion.div
                    key="chatbot"
                    variants={chatbotVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    style={{ position: "fixed", inset: 0, zIndex: 50 }}
                >
                    <div className="gt-chatbot">
                        {checkingConsent && (
                            <div className="gt-auth-modal-backdrop" aria-busy="true">
                                <div className="gt-auth-modal gt-auth-modal--loading">
                                    <div className="gt-consent-loading-spinner" aria-hidden="true" />
                                    <p>Loading…</p>
                                </div>
                            </div>
                        )}
                        {!checkingConsent && showGate && (
                            <div className="gt-auth-modal-backdrop">
                                <div className="gt-auth-modal">
                                    <h2>{needsAuth ? "Sign in to continue" : "Chat consent required"}</h2>
                                    <p>
                                        {needsAuth
                                            ? "Use your SSO account to access the Lab Buddy chatbot."
                                            : "By using this chatbot, you acknowledge that your conversations may be stored in our secure database in anonymized form. We remove direct identifiers and use the conversations to improve the chatbot's performance and learning support. Your instructor may see your conversations with the chatbot but the data will be anonymous, unless you share your own name or other identifiers in your messages to the chatbot."}
                                    </p>
                                    <button
                                        className="gt-auth-primary-btn"
                                        onClick={needsAuth ? redirectToSSO : acceptChatConsent}
                                    >
                                        {needsAuth ? "Sign in with SSO" : "I accept"}
                                    </button>
                                </div>
                            </div>
                        )}
                    {!showGate && !checkingConsent && (
                    <>
                    {/* Sidebar */}
                    <aside className={`gt-sidebar ${sidebarOpen ? "open" : "closed"}`}>
                        <div className="gt-sidebar-header">
                        <img src="/favicon.ico" alt="Logo" className="gt-sidebar-logo" />
                        <button onClick={() => setSidebarOpen(false)} className="gt-hide-sidebar-btn">
                            <svg className="gt-icon-md" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        </div>
                        
                        <div className="gt-sidebar-header">
                                <button onClick={createNewChat} className="gt-new-chat-btn">
                            <svg className="gt-icon-md" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                            </svg>
                            New Chat
                        </button>
                        </div>

                        <div className="gt-conversation-list">
                        <p className="gt-section-label">Recent</p>
                        <ul>
                            {conversations.map((conv) => (
                            <li key={conv.session_id} className="gt-conversation-item">
                                <button
                                onClick={() =>
                                    conv._pending
                                        ? setActiveConversationId(conv.session_id)
                                        : loadSessionMessages(conv.session_id)
                                }
                                className={`gt-conversation-btn ${conv.session_id === activeConversationId ? "active" : ""}`}
                                >
                                <svg className="gt-icon-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                                    d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                </svg>
                                <span>{conv.title}</span>
                                </button>
                                {conversations.length > 1 && (
                                <button onClick={() => deleteConversation(conv.session_id)} className="gt-delete-btn">
                                    <svg className="gt-icon-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: "#9ca3af" }}>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                                )}
                            </li>
                            ))}
                        </ul>
                        </div>

                        <div className="gt-sidebar-footer">
                        <div className="gt-user-info">
                            <div className="gt-avatar">
                            <span>{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                            </div>
                            <div>
                            <p className="gt-user-name">{user?.name || 'User'}</p>
                            </div>
                        </div>
                        </div>
                    </aside>

                    {/* Main Content - rest remains the same but update condition checks */}
                    <main className="gt-main">
                        <header className="gt-header">
                        <div className="gt-header-left">
                            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="gt-menu-btn">
                            <svg className="gt-icon-md" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                            </button>
                            <h1 className="gt-header-title">{activeConversation?.title || "New Chat"}</h1>
                        </div>
                        <div className="gt-header-actions">
                            <button
                                type="button"
                                className="gt-back-btn"
                                onClick={() => {
                                    // if (roleData === "instructor") navigate("/instructor");
                                    // else if (roleData === "student") navigate("/dashboard");
                                    // else navigate("/");
                                    if (roleData === "instructor") handleNavigateAway("/instructor");
                                    else if (roleData === "student") handleNavigateAway("/dashboard");
                                    else handleNavigateAway("/");
                                }}
                            >
                                {roleData === "instructor" ? "Instructor Dashboard" : roleData === "student" ? "Student Dashboard" : "Home"}
                            </button>
                            <button type="button" className="gt-logout-btn" onClick={logout}>
                                Logout
                            </button>
                        </div>
                        </header>

                        <div className="gt-chat-area"  ref={scrollContainerRef}>
                        {!activeConversation || activeConversation.messages.length === 0 ? (
                            <div className="gt-empty-state">
                            <div className="gt-empty-icon">
                                <svg className="gt-icon-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                    d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                </svg>
                            </div>
                            <h2 className="gt-empty-title">How can I help you today?</h2>
                            <p className="gt-empty-text">Ask me anything about the lab you are working on!</p>
                            </div>
                        ) : (
                            <div className="gt-messages">
                            {activeConversation.messages.map((message) => (
                                <div key={message.id} className={`gt-message ${message.role}`}>
                                <div className="gt-message-inner">
                                    <div className="gt-message-avatar">
                                    {message.role === "user" ? (
                                        <svg className="gt-icon-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    ) : (
                                        <svg className="gt-icon-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}>
                                        <rect x="3" y="7" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 7V5a2 2 0 012-2h6a2 2 0 012 2v2" />
                                        <circle cx="9" cy="12" r="1" />
                                        <circle cx="15" cy="12" r="1" />
                                        <path d="M9 16h6" />
                                        </svg>
                                    )}
                                    </div>
                                    <div className="gt-message-content">
                                    {message._loading ? (
                                        <p className="gt-message-text gt-typing-indicator">
                                            <span className="gt-typing-dot" />
                                            <span className="gt-typing-dot" />
                                            <span className="gt-typing-dot" />
                                        </p>
                                    ) : (
                                        <div className="gt-message-text">
                                            <ReactMarkdown>{message.content}</ReactMarkdown>
                                        </div>
                                    )}
                                    </div>
                                </div>
                                </div>
                            ))}
                            </div>
                        )}
                        </div>

                        <div className="gt-input-area">
                        <form onSubmit={handleSubmit} className="gt-input-form">
                            <div className="gt-input-wrapper">
                            <textarea
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSubmit(e);
                                }
                                }}
                                placeholder="Message Lab Assistant..."
                                rows={1}
                                className="gt-input"
                                            disabled={showGate || loading || !activeConversationId}
                            />
                            </div>
                                    <button type="submit" disabled={!input.trim() || loading || !activeConversationId || showGate} className="gt-send-btn">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M12 5l7 7-7 7" />
                            </svg>
                            </button>
                        </form>
                        </div>
                    </main>
                    </>
                    )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}